import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, Clock, Mail, Phone, Home, Copy, Shield, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { log } from '../../lib/logger';
import api from '../../api';

// External effect body — keeps the useCallback body trivial.
const fetchCustomers = async (filter, setRows, setLoading) => {
  setLoading(true);
  try {
    const params = filter === 'all' ? {} : { status: filter };
    const { data } = await api.get('/customers', { params });
    setRows(data);
  } catch (err) {
    log.error('Customers load failed', err);
    toast.error('Could not load customers');
  } finally {
    setLoading(false);
  }
};

const CustomersTab = () => {
  const [filter, setFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => fetchCustomers(filter, setRows, setLoading), [filter, fetchCustomers, setRows, setLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (err) {
      log.warn('Clipboard write failed', err);
      toast.error('Could not copy');
    }
  };

  const manuallyVerify = async (c) => {
    if (!window.confirm(`Manually verify ${c.full_name || c.email}? They will be able to sign in immediately.`)) return;
    try {
      const { data } = await api.put(`/customers/${c.id}/verify`);
      setRows((prev) => prev.map((r) => (r.id === c.id ? data : r)));
      toast.success(`${c.full_name || 'Customer'} verified`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to verify');
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Permanently delete ${c.full_name || c.email}?\n\nThis will also delete ALL of their orders. This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      setRows((prev) => prev.filter((r) => r.id !== c.id));
      toast.success(`${c.full_name || 'Customer'} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const wipeAllTestData = async () => {
    const confirmed = window.prompt(
      'WIPE all customers and orders?\n\nThis cannot be undone.\n\nType WIPE to confirm:',
    );
    if (confirmed !== 'WIPE') {
      if (confirmed !== null) toast.message('Wipe cancelled — type WIPE exactly to confirm.');
      return;
    }
    try {
      const { data } = await api.post('/admin/danger/wipe-test-data');
      toast.success(`Wiped ${data.orders_deleted} orders and ${data.customers_deleted} customers.`);
      setRows([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to wipe');
    }
  };

  const counts = {
    verified: rows.filter((r) => r.phone_verified).length,
    pending: rows.filter((r) => !r.phone_verified).length,
  };

  return (
    <div data-testid="admin-customers-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0a0a0a]">Customers <span className="text-slate-400 font-normal">· {rows.length}</span></h2>
          <p className="text-sm text-slate-500">Everyone who's submitted the signup form. Pending leads haven't verified by SMS yet.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg" data-testid="customers-refresh-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Danger zone — wipe everything (idempotent server-side, prompts client-side) */}
      <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-3.5 flex items-center justify-between gap-3 flex-wrap" data-testid="danger-zone">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-900">Danger zone</p>
            <p className="text-xs text-red-700">Wipe ALL test/fake customer accounts and orders to start with a clean slate. Products &amp; admin users are preserved.</p>
          </div>
        </div>
        <button
          onClick={wipeAllTestData}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg flex-shrink-0"
          data-testid="wipe-test-data-btn"
        >
          <Trash2 size={13} /> Wipe all customers &amp; orders
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-4 border-b border-slate-200">
        <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')}>All ({rows.length})</FilterBtn>
        <FilterBtn active={filter === 'pending'} onClick={() => setFilter('pending')}>Pending leads ({counts.pending})</FilterBtn>
        <FilterBtn active={filter === 'verified'} onClick={() => setFilter('verified')}>Verified ({counts.verified})</FilterBtn>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-red-500" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-sm text-slate-500">
          No customers in this filter yet.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {rows.map((c) => (
            <CustomerRow key={c.id} c={c} onCopy={copy} onManualVerify={manuallyVerify} onDelete={deleteCustomer} />
          ))}
        </div>
      )}
    </div>
  );
};

const FilterBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
      active ? 'border-red-500 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-800'
    }`}
    data-testid={`customers-filter-${typeof children === 'string' ? children.toLowerCase() : ''}`}
  >
    {children}
  </button>
);

const CustomerRow = ({ c, onCopy, onManualVerify, onDelete }) => {
  const date = c.created_at ? new Date(c.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return (
    <div className="px-4 py-4 hover:bg-slate-50 transition-colors" data-testid={`customer-row-${c.email}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#0a0a0a]">{c.full_name || '(no name)'}</span>
            {c.phone_verified ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={11} /> Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <Clock size={11} /> Pending verification
              </span>
            )}
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <DetailRow icon={Mail} label="Email" value={c.email} onCopy={() => onCopy(c.email, 'Email')} />
            <DetailRow icon={Phone} label="Phone" value={c.phone || '—'} onCopy={c.phone ? () => onCopy(c.phone, 'Phone') : undefined} />
            <DetailRow icon={Home} label="Address" value={c.address || '—'} onCopy={c.address ? () => onCopy(c.address, 'Address') : undefined} fullWidth />
          </div>
          <div className="mt-2 text-[11px] text-slate-400">Signed up: {date}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!c.phone_verified && (
            <button
              onClick={() => onManualVerify(c)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg shadow-sm"
              data-testid={`manually-verify-${c.email}`}
              title="Skip SMS — manually mark this customer as verified"
            >
              <Shield size={12} /> Manually verify
            </button>
          )}
          <button
            onClick={() => onDelete(c)}
            className="inline-flex items-center gap-1.5 p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg"
            data-testid={`delete-customer-${c.email}`}
            title="Permanently delete this customer + their orders"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, onCopy, fullWidth }) => (
  <div className={`flex items-center gap-2 ${fullWidth ? 'sm:col-span-2' : ''}`}>
    <Icon size={13} className="text-slate-400 flex-shrink-0" />
    <span className="text-slate-700 truncate" title={value}>{value}</span>
    {onCopy && (
      <button onClick={onCopy} className="text-slate-300 hover:text-red-600 flex-shrink-0" aria-label={`Copy ${label}`}>
        <Copy size={11} />
      </button>
    )}
  </div>
);

export default CustomersTab;
